// "Install the app" banner for phones (PWA).
// Android / Chrome / Edge: uses the browser's own install dialog.
// iPhone / iPad (Safari has no install dialog): explains Share → Add to Home Screen.
// Hidden when already installed, on larger screens, or for 14 days after "Not now".
import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from './Icon';
import { faXmark, faArrowUpFromBracket, faSquarePlus, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useI18n } from '../lib/i18n';
import { storageGet, storageSet } from '../lib/format';

const KEY = 'tev_install_dismissed';
const SNOOZE_DAYS = 14;

const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export default function InstallPrompt() {
  const { t } = useI18n();
  const [deferred, setDeferred] = useState(null);
  const [ios, setIos] = useState(false);
  const [hidden, setHidden] = useState(() => isStandalone() || Date.now() - storageGet(KEY, 0) < SNOOZE_DAYS * 864e5);

  useEffect(() => {
    if (hidden) return undefined;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setHidden(true);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    // Safari on iOS never fires beforeinstallprompt; show instructions after a short delay.
    const timer = isIos() ? setTimeout(() => setIos(true), 2500) : null;
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      clearTimeout(timer);
    };
  }, [hidden]);

  if (hidden || (!deferred && !ios)) return null;
  const dismiss = () => { storageSet(KEY, Date.now()); setHidden(true); };
  const install = async () => {
    deferred.prompt();
    const { outcome } = await deferred.userChoice.catch(() => ({ outcome: 'dismissed' }));
    setDeferred(null);
    if (outcome === 'accepted') setHidden(true); else dismiss();
  };

  return (
    <div role="dialog" aria-label={t('Install the Tatheer app')}
      className="fade-up fixed inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-50 rounded-2xl border border-slate-200 bg-white p-4 shadow-lift sm:hidden">
      <div className="flex items-start gap-3">
        <img src="/icons/icon-192.png" alt="" className="size-12 shrink-0 rounded-xl border border-slate-200" />
        <div className="min-w-0 flex-1">
          <p className="font-bold">{t('Install the Tatheer app')}</p>
          {deferred ? (
            <p className="text-[13px] text-slate-500">{t('Shop faster from your home screen – it opens like an app.')}</p>
          ) : (
            <p className="text-[13px] text-slate-500">
              {t('Tap')} <FontAwesomeIcon icon={faArrowUpFromBracket} className="mx-0.5 text-brand-600" /> {t('Share, then')} <FontAwesomeIcon icon={faSquarePlus} className="mx-0.5 text-brand-600" /> <b>{t('Add to Home Screen')}</b>.
            </p>
          )}
        </div>
        <button type="button" onClick={dismiss} aria-label={t('Close')} className="-m-1 grid size-8 shrink-0 cursor-pointer place-items-center rounded-lg text-slate-400 hover:bg-slate-100">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      {deferred && (
        <div className="mt-3 flex gap-2">
          <button type="button" onClick={dismiss} className="btn btn-outline btn-sm flex-1">{t('Not now')}</button>
          <button type="button" onClick={install} className="btn btn-primary btn-sm flex-1"><FontAwesomeIcon icon={faDownload} /> {t('Install app')}</button>
        </div>
      )}
    </div>
  );
}
